import { useEffect } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for the Sideform
import SideformRoot from "./SideformRoot";

// Material Dashboard 2 PRO React context
import { useMaterialUIController, setMiniSideform } from "contexts/context";

// Declaring props types for Sidenav
interface Props {
  title?: string;
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  children: React.ReactNode;
  [key: string]: any;
}

function Sideform({ title, color, children, ...rest }: Props): JSX.Element {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSideform, darkMode } = controller;
  const location = useLocation();

  const closeSideform = () => setMiniSideform(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sideform.
    function handleMiniSideform() {
      setMiniSideform(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSideform);

    // Call the handleMiniSideform function to set the state with the initial value.
    handleMiniSideform();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSideform);
  }, [dispatch, location]);

  return (
    <SideformRoot
      {...rest}
      variant="permanent"
      anchor="right"
      ownerState={{ miniSideform, darkMode }}
    >
      <MDBox py={3} px={1} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSideform}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h5" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDTypography component="h5" variant="h5" fontWeight="medium" mb={1}>
          {title}
        </MDTypography>
        {children}
      </MDBox>
    </SideformRoot>
  );
}

// Declaring default props for Sideform
Sideform.defaultProps = {
  title: "Data Selection",
  color: "info",
  brand: "",
};

export default Sideform;
